import { useIsMobile } from "@/hooks/use-mobile";
import { usesMobileApp } from "@/lib/mobile-app";
import { useTransportMode } from "@/hooks/use-transport-mode";
import { useGlobalState } from "@/lib/states";
import { getPageConfig, type PageConfig } from "@/lib/page-config";
import { useCompany } from "@/query/hooks/useCompany";
import { useCompanyPersona } from "@/hooks/use-company-persona";
import { MapPin, Truck } from "lucide-react";
import { usePathname } from "@/hooks/use-pathname-compat";
import { useSearchParams } from "@/hooks/use-search-params-compat";
import React from "react";
import { useTranslation } from "react-i18next";

export function Header({
  children,
}: Readonly<{
  children: React.ReactNode;
}>) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const chatId = searchParams.get("c");
  const { t } = useTranslation();
  const [globalSelectedChat] = useGlobalState("selected_chat", null);
  const isMobile = useIsMobile();
  // The company hub titles its header with the live company name (falls back to
  // the static page-config title before the company loads). Gated to /company so
  // the app shell doesn't fetch `getMe` on every route just for that one title.
  const { data: company } = useCompany({
    enabled: pathname.startsWith("/company"),
  });
  // An agency admin's /company view owns its own single header (the agency
  // detail / empty), so the app title bar is dropped for those personas. Gated
  // to /company so this doesn't query groups on every route.
  const { view, isResolving } = useCompanyPersona({
    enabled: pathname.startsWith("/company"),
  });
  // Only the company hub keeps the app title bar; the agency workspace owns its
  // own header and the empty state shows nothing. While resolving, hide it so a
  // workspace user's bar doesn't flash in then out.
  const showAppTitleBar = view === "hub" && !isResolving;

  const isDetailView =
    // Chat detail (internal chat with URL param)
    (pathname.startsWith("/chat") && chatId !== null) ||
    // Product detail
    pathname.match(/^\/products\/[^\/]+$/) ||
    // Order detail
    pathname.match(/^\/orders\/[^\/]+$/) ||
    // Purchase detail
    pathname.match(/^\/purchases\/[^\/]+$/) ||
    // Saved offer detail
    pathname.match(/^\/saved-offers\/[^\/]+$/) ||
    // Saved offers main page
    pathname === "/saved-offers" ||
    // Profile detail (other users' profiles)
    pathname.match(/^\/profile\/[^\/]+$/) ||
    // My profile page
    pathname === "/profile" ||
    // Contacts detail view
    pathname === "/contacts" ||
    // CRM detail view
    pathname.match(/^\/crm\/[^\/]+$/) ||
    // Recap list view AND focus view both manage their own header
    // (RecapHeader inside RecapShell; back/partner header inside
    // PreOrderFocusView). Treating both as "detail" keeps PageContent's
    // tree structurally consistent between /recap and /recap/:id —
    // React Router's nested-route layout (RecapWorkspaceLayout, chat
    // panel) only persists across the navigation when the parent tree
    // doesn't reconcile to a different shape on every route flip.
    pathname === "/recap" ||
    (pathname.startsWith("/recap/") && pathname !== "/recap/mobile") ||
    // /purchases list mirrors /recap — it mounts the same workspace
    // layout (with mode="purchases") and surfaces its own RecapHeader,
    // so the page-level title chrome would double up. /purchases/:id is
    // already covered by the `/^\/purchases\/[^\/]+$/` match above.
    pathname === "/purchases" ||
    // /recap_transport is a sibling page reusing the recap shell (it mounts
    // the provider with mode:null, not a recap mode) — same RecapHeader on
    // both list AND focus routes, so the global
    // page-title chrome would double up. `/recap_transport/mobile` is
    // excluded to match the /recap/mobile exception above (mobile owns
    // its own bar).
    pathname === "/recap_transport" ||
    (pathname.startsWith("/recap_transport/") &&
      pathname !== "/recap_transport/mobile") ||
    // Preferences owns its own compact header (back + title).
    pathname === "/preferences" ||
    pathname.startsWith("/preferences/") ||
    // /dictionary owns its full height: a sticky title block + a sticky column
    // header over an internally-scrolling table. The app title bar would both
    // double the title and take the height that sticky needs. (It also has no
    // `pageConfigs` entry, so the bar was rendering the "Menu" fallback.)
    pathname === "/dictionary" ||
    // Steering (BI) owns its own header: the dashboard tab strip IS the page
    // header, so the global page-title chrome would double up. Covers both the
    // index (redirects to a tab) and each `/steering/:id` board.
    pathname === "/steering" ||
    pathname.startsWith("/steering/");

  // Special case for chat page: hide header on mobile when a chat is selected
  const isChatViewOnMobile =
    pathname.startsWith("/chat") && globalSelectedChat !== null;

  const isTransportMode = useTransportMode();

  // The lean mobile app (Capacitor or phone viewport) — every surface owns its
  // own top bar (mobile chat / proposals / list headers), so the desktop page
  // header chrome must not render on top of them. Keep a height-bound
  // container so children using `h-full` still measure correctly. Must be
  // `h-full` (the MobileAppShell flex slot, which already subtracts the
  // bottom nav), NOT `h-screen`: 100vh is the iOS LARGE viewport, taller
  // than the visible area — it made the document itself scroll (header
  // dragging down) and cropped the bottom-docked composer.
  if (usesMobileApp())
    return <div className="h-full overflow-hidden">{children}</div>;

  // Detail views render their own header — but still need a viewport-bound
  // container so children using `h-full` (e.g. RecapWorkspaceLayout) have
  // something to measure against. Without this the page would expand to
  // fit content and the whole window would scroll instead of the inner
  // list / panel scrolling internally.
  // The height subtracts the impersonation banner (the var is 0 outside
  // impersonation): this container sits INSIDE the already-offset sidebar
  // shell, so a raw 100vh would overflow the viewport by the banner's height
  // and let a stray wheel event drag the page — burying this view's own
  // header chrome under the fixed banner.
  if (isDetailView)
    return (
      <div className="h-[calc(100vh_-_var(--impersonation-banner-height))] overflow-hidden">
        {children}
      </div>
    );

  // Get page config for current path, with transport mode overrides
  const baseConfig = getPageConfig(pathname);
  const transportOverrides: Record<string, Partial<PageConfig>> = {
    "/products": {
      icon: MapPin,
      titleKey: "navigation.products_transport",
    },
    "/partners": { titleKey: "navigation.partners_transport" },
    "/recap": { icon: Truck, titleKey: "navigation.recap_transport" },
    "/purchases": {
      icon: Truck,
      titleKey: "navigation.purchases_transport",
    },
  };

  const basePath = "/" + pathname.split("/")[1];
  const override =
    isTransportMode && baseConfig
      ? transportOverrides[pathname] || transportOverrides[basePath]
      : undefined;
  const pageConfig = override ? { ...baseConfig!, ...override } : baseConfig;
  const PageIcon = pageConfig?.icon;

  // Get page title based on path
  const getPageTitle = () => {
    if (pathname.startsWith("/company") && company?.name) {
      return company.name;
    }
    if (pageConfig) {
      return t(pageConfig.titleKey);
    }
    return t("navigation.menu");
  };

  // Get page subtitle if available
  const getPageSubtitle = () => {
    if (pageConfig?.subtitleKey) {
      return t(pageConfig.subtitleKey, "");
    }
    return null;
  };

  const pageSubtitle = getPageSubtitle();

  // Pages that fill the viewport and manage their own inner scroll
  // (sticky headers, virtualised lists, side panels). Their root sets
  // `h-full overflow-hidden`, so the parent wrapper here must stay
  // overflow-hidden too — otherwise the outer container becomes a
  // second scroll target with nothing to scroll, producing the
  // rubber-band "bounce" effect on macOS/iOS browsers and pushing the
  // page header up and down on every wheel/trackpad nudge.
  const selfScrolls = pathname === "/recap";

  return (
    <div className="flex flex-col h-screen max-h-screen">
      {showAppTitleBar && (
        <header
          className={`flex-shrink-0 bg-white z-40 ${
            // The company hub folds its tab strip directly under the title, so the
            // header drops its own divider/shadow — the tab underline is the only
            // separator (avoids a heavy double line).
            pathname.startsWith("/company")
              ? ""
              : "border-b border-green-300 shadow-sm"
          } ${isChatViewOnMobile ? "hidden md:block" : ""}`}
        >
          <div className="flex items-center justify-between h-14 sm:h-16 px-4 sm:px-6">
            <div className="flex items-center gap-3">
              {/* Icon */}
              {PageIcon && (
                <div className={`p-2 ${pageConfig?.iconBgColor} rounded-lg`}>
                  <PageIcon
                    className={`h-5 w-5 sm:h-6 sm:w-6 ${pageConfig?.iconColor}`}
                  />
                </div>
              )}
              {/* Title and Subtitle */}
              <div className="flex flex-col">
                <div className="font-semibold text-base sm:text-xl text-foreground flex items-center">
                  {getPageTitle()}
                </div>
                {pageSubtitle && !isMobile && (
                  <p className="text-xs text-gray-500 hidden sm:block">
                    {pageSubtitle}
                  </p>
                )}
              </div>
            </div>

            {/* AI assistant moved to the global floating launcher (STK-356). */}
          </div>
        </header>
      )}

      <div
        className={`flex-1 min-h-0 ${selfScrolls ? "overflow-hidden" : "overflow-auto"}`}
      >
        {children}
      </div>
    </div>
  );
}
